import React, { useMemo } from 'react';
import {
  getMonthDays, getMondayBasedIndex, toISODate, parseShiftTime,
  getStatusColor, formatHours, shortLocationCode
} from '../../../utils/grafikHelpers';

/**
 * Widok macierzowy: wszyscy pracownicy (wiersze) x dni miesiaca (kolumny).
 * Komorka: skrot zmiany lub status (urlop/wolne/chorobowe/nieobecnosc).
 * Klik pusta komorka -> create, klik z zmiana -> edit.
 *
 * Props:
 *   year, month
 *   users: [{login, typ}]
 *   shifts: [{id, user_login, location_id, location_nazwa, data, godzina_od, godzina_do, status, ...}]
 *   onEditShift(shift)
 *   onAddShift({user_login, date})  - data jako string YYYY-MM-DD
 */
const ABSENCE_LABELS = {
  urlop: 'URL',
  chorobowe: 'L4',
  nieobecnosc: 'NB',
  wolne: '—'
};

const WEEKDAY_HEADERS = ['Pn', 'Wt', 'Sr', 'Cz', 'Pt', 'So', 'Nd'];

// Kolory wg dostepnosci pracownika
const AVAIL_COLOR = {
  dostepny: '#28a745',
  niedostepny: '#dc3545',
  preferowany: '#ffc107'
};
const AVAIL_EMOJI = {
  dostepny: '🟢',
  niedostepny: '🔴',
  preferowany: '⭐'
};
const AVAIL_LABEL = {
  dostepny: 'D',     // skroty na matrixie
  niedostepny: 'N',
  preferowany: 'P'
};

const AllEmployeesMatrix = ({ year, month, users = [], shifts = [], availability = {}, onEditShift, onAddShift }) => {
  const days = useMemo(() => getMonthDays(year, month), [year, month]);
  const todayIso = toISODate(new Date());

  // Index: {user_login}_{iso} -> [shift, shift, ...]
  const byCell = useMemo(() => {
    const acc = {};
    (shifts || []).forEach(s => {
      const date = String(s.data || '').slice(0, 10);
      if (!date || !s.user_login) return;
      const key = `${s.user_login}|${date}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(s);
    });
    return acc;
  }, [shifts]);

  // Suma godzin per pracownik
  const sumByUser = useMemo(() => {
    const acc = {};
    (shifts || []).forEach(s => {
      const u = s.user_login;
      if (!u) return;
      const h = parseFloat(s.godziny_planowane || 0) || 0;
      acc[u] = (acc[u] || 0) + h;
    });
    return acc;
  }, [shifts]);

  // Cell - pojedyncza komorka pracownik/dzien
  const renderCell = (userLogin, date) => {
    const iso = toISODate(date);
    const cellShifts = byCell[`${userLogin}|${iso}`] || [];
    const isToday = iso === todayIso;
    const isWeekend = getMondayBasedIndex(date) >= 5;
    const isEmpty = cellShifts.length === 0;

    // Dostepnosc pracownika tego dnia (bierzemy pierwszy wpis)
    const userAvail = (availability[userLogin] || {})[iso] || [];
    const availEntry = userAvail[0] || null;
    const availColor = availEntry ? AVAIL_COLOR[availEntry.typ] : null;
    const availLabel = availEntry ? AVAIL_LABEL[availEntry.typ] : '';
    const availEmojiCh = availEntry ? AVAIL_EMOJI[availEntry.typ] : '';
    const availTooltip = userAvail.map(e => {
      const lbl = e.typ === 'dostepny' ? 'Dostepny'
                : e.typ === 'niedostepny' ? 'Niedostepny'
                : 'Preferowany';
      const hours = e.godzina_od && e.godzina_do ? ` ${e.godzina_od}-${e.godzina_do}` : '';
      const note = e.notatka ? ` "${e.notatka}"` : '';
      return `${lbl}${hours}${note}`;
    }).join('\n');

    const baseStyle = {
      width: '46px',
      minWidth: '46px',
      height: '44px',
      border: '1px solid var(--border)',
      borderTop: isToday ? '2px solid var(--info)' : '1px solid var(--border)',
      backgroundColor: isWeekend ? 'var(--bg)' : 'var(--card-bg)',
      padding: '2px',
      fontSize: '0.62rem',
      fontWeight: 500,
      cursor: 'pointer',
      verticalAlign: 'top',
      position: 'relative',
      overflow: 'hidden'
    };

    // Maly badge dostepnosci w prawym gornym rogu komorki
    const availBadge = availEntry && (
      <span
        title={availTooltip}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          backgroundColor: availColor,
          color: '#fff',
          fontSize: '0.55rem',
          fontWeight: 700,
          padding: '0 3px',
          borderRadius: '0 0 0 4px',
          lineHeight: '11px',
          pointerEvents: 'none',
          zIndex: 1
        }}
      >
        {availLabel}
      </span>
    );

    if (isEmpty) {
      // Pusta komorka - jesli jest dostepnosc, podkresl ja tlem
      return (
        <td
          key={iso}
          style={{
            ...baseStyle,
            backgroundColor: availEntry ? `${availColor}22` : baseStyle.backgroundColor,
            borderLeft: availEntry ? `3px solid ${availColor}` : '1px solid var(--border)'
          }}
          onClick={() => onAddShift && onAddShift({ user_login: userLogin, date: iso })}
          title={availEntry ? availTooltip + '\n(klik = dodaj zmiane)' : `${userLogin} ${iso} (klik = dodaj)`}
        >
          {availBadge}
          {availEntry ? (
            <div style={{
              color: availColor, fontWeight: 700, fontSize: '0.6rem',
              textAlign: 'center', marginTop: '10px'
            }}>
              {availEmojiCh}
            </div>
          ) : (
            <span style={{ color: 'var(--text-muted)', opacity: 0.3, fontSize: '0.65rem' }}>+</span>
          )}
        </td>
      );
    }

    if (cellShifts.length === 1) {
      const s = cellShifts[0];
      const isAbsence = ['urlop', 'chorobowe', 'nieobecnosc', 'wolne'].includes(s.status);
      const od = parseShiftTime(s.godzina_od);
      const doG = parseShiftTime(s.godzina_do);
      const isAccepted = !!(s.zaakceptowana_przez_pracownika === 1
        || s.zaakceptowana_przez_pracownika === true
        || s.zaakceptowana_przez_pracownika === '1');

      // Wybor koloru:
      //  - absencja: kolor wg statusu
      //  - zaakceptowana: zielone tlo
      //  - niezaakceptowana (planowana/zatwierdzona): zolte tlo
      let bgColor, borderColor, hoverHint;
      if (isAbsence) {
        const c = getStatusColor(s.status);
        bgColor = `${c}22`;
        borderColor = c;
        hoverHint = '';
      } else if (isAccepted) {
        bgColor = 'rgba(40, 167, 69, 0.30)';   // zielony 30%
        borderColor = '#28a745';
        hoverHint = ' [zaakceptowana]';
      } else {
        bgColor = 'rgba(255, 193, 7, 0.30)';   // zolty 30%
        borderColor = '#ffc107';
        hoverHint = ' [czeka na akceptacje]';
      }

      return (
        <td
          key={iso}
          style={{
            ...baseStyle,
            backgroundColor: bgColor,
            borderLeft: `3px solid ${borderColor}`
          }}
          onClick={() => onEditShift && onEditShift(s)}
          title={`${userLogin} ${iso} ${od}-${doG} ${s.location_nazwa || ''}${hoverHint}${availTooltip ? '\n' + availTooltip : ''} (klik = edytuj)`}
        >
          {availBadge}
          {isAbsence ? (
            <div style={{ color: borderColor, fontWeight: 700, textAlign: 'center', marginTop: '8px' }}>
              {ABSENCE_LABELS[s.status] || s.status?.slice(0, 3).toUpperCase()}
            </div>
          ) : (
            <>
              <div style={{ color: 'var(--text)', fontWeight: 700, lineHeight: '1.1' }}>
                {od?.replace(':', '')}-{doG?.replace(':', '')}
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.55rem', marginTop: '1px' }}>
                {shortLocationCode(s.location_nazwa)}
              </div>
            </>
          )}
        </td>
      );
    }

    // Wiele zmian w 1 dniu
    const anyNotAccepted = cellShifts.some(s => !(
      s.zaakceptowana_przez_pracownika === 1 ||
      s.zaakceptowana_przez_pracownika === true ||
      s.zaakceptowana_przez_pracownika === '1'
    ));
    const multiBg = anyNotAccepted ? 'rgba(255, 193, 7, 0.30)' : 'rgba(40, 167, 69, 0.30)';
    const multiBorder = anyNotAccepted ? '#ffc107' : '#28a745';

    return (
      <td
        key={iso}
        style={{
          ...baseStyle,
          backgroundColor: multiBg,
          borderLeft: `3px solid ${multiBorder}`
        }}
        onClick={() => onEditShift && onEditShift(cellShifts[0])}
        title={cellShifts.map(s => `${parseShiftTime(s.godzina_od)}-${parseShiftTime(s.godzina_do)} ${s.location_nazwa || ''}`).join('\n') + (availTooltip ? '\n' + availTooltip : '')}
      >
        {availBadge}
        <div style={{ color: multiBorder, fontWeight: 700, fontSize: '0.7rem' }}>
          {cellShifts.length}× zm.
        </div>
      </td>
    );
  };

  if (!users || users.length === 0) {
    return (
      <div style={{
        padding: '2rem', textAlign: 'center', color: 'var(--text-muted)',
        backgroundColor: 'var(--bg)', borderRadius: '4px', fontSize: '0.9rem'
      }}>
        Brak pracownikow do wyswietlenia. Wybierz lokalizacje powyzej.
      </div>
    );
  }

  return (
    <div style={{
      overflow: 'auto', maxHeight: 'calc(100vh - 300px)',
      border: '1px solid var(--border)', borderRadius: '4px',
      backgroundColor: 'var(--card-bg)'
    }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem' }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 3, backgroundColor: 'var(--bg)' }}>
          <tr>
            <th style={{
              position: 'sticky', left: 0, zIndex: 4, backgroundColor: 'var(--bg)',
              padding: '0.5rem 0.75rem', textAlign: 'left', minWidth: '180px',
              borderRight: '2px solid var(--border)', borderBottom: '2px solid var(--border)',
              fontWeight: 700
            }}>
              Pracownik
            </th>
            {days.map(date => {
              const dow = getMondayBasedIndex(date);
              const isWeekend = dow >= 5;
              const iso = toISODate(date);
              const isToday = iso === todayIso;
              return (
                <th key={iso} style={{
                  padding: '4px 2px', textAlign: 'center', minWidth: '46px',
                  borderBottom: '2px solid var(--border)',
                  backgroundColor: isToday ? 'var(--info)' : (isWeekend ? 'var(--bg)' : 'var(--bg)'),
                  color: isToday ? '#fff' : (isWeekend ? 'var(--text-muted)' : 'var(--text)'),
                  fontWeight: 700, fontSize: '0.7rem'
                }}>
                  <div>{date.getDate()}</div>
                  <div style={{ fontSize: '0.55rem', fontWeight: 500, opacity: 0.8 }}>
                    {WEEKDAY_HEADERS[dow]}
                  </div>
                </th>
              );
            })}
            <th style={{
              position: 'sticky', right: 0, zIndex: 4, backgroundColor: 'var(--bg)',
              padding: '0.5rem 0.5rem', textAlign: 'center', minWidth: '60px',
              borderLeft: '2px solid var(--border)', borderBottom: '2px solid var(--border)',
              fontWeight: 700
            }}>
              Σ
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.login}>
              <td style={{
                position: 'sticky', left: 0, zIndex: 2,
                backgroundColor: 'var(--card-bg)',
                padding: '0.4rem 0.75rem', borderRight: '2px solid var(--border)',
                borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap'
              }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.8rem' }}>
                  {u.login}
                </div>
                {u.typ && (
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                    {u.typ}
                  </div>
                )}
              </td>
              {days.map(date => renderCell(u.login, date))}
              <td style={{
                position: 'sticky', right: 0, zIndex: 2,
                backgroundColor: 'var(--card-bg)',
                padding: '0.4rem 0.5rem', borderLeft: '2px solid var(--border)',
                borderBottom: '1px solid var(--border)',
                textAlign: 'center', fontWeight: 700, color: 'var(--text)'
              }}>
                {formatHours(sumByUser[u.login] || 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Legenda */}
      <div style={{
        padding: '0.6rem 0.75rem',
        borderTop: '1px solid var(--border)',
        backgroundColor: 'var(--bg)',
        display: 'flex', flexWrap: 'wrap', gap: '1rem',
        fontSize: '0.7rem', color: 'var(--text-secondary)'
      }}>
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>Legenda:</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ width: '14px', height: '14px', backgroundColor: 'rgba(255,193,7,0.30)', borderLeft: '3px solid #ffc107' }} />
          Zmiana ustawiona (czeka na akceptacje)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ width: '14px', height: '14px', backgroundColor: 'rgba(40,167,69,0.30)', borderLeft: '3px solid #28a745' }} />
          Zaakceptowana przez pracownika
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ width: '14px', padding: '0 3px', backgroundColor: '#28a745', color: '#fff', fontWeight: 700, fontSize: '0.6rem', textAlign: 'center' }}>D</span>
          Dostepny
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ width: '14px', padding: '0 3px', backgroundColor: '#dc3545', color: '#fff', fontWeight: 700, fontSize: '0.6rem', textAlign: 'center' }}>N</span>
          Niedostepny
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ width: '14px', padding: '0 3px', backgroundColor: '#ffc107', color: '#fff', fontWeight: 700, fontSize: '0.6rem', textAlign: 'center' }}>P</span>
          Preferowany
        </span>
      </div>
    </div>
  );
};

export default AllEmployeesMatrix;
